/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 *
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @version 1.13.0
 * @since 1.0.1
 */

import { gtmOperationPush, IGTMOperation } from './index';

/**
 * ------------------------------------
 *  CLICK OPERATIONS
 *  -----------------------------------
 */

/**
 * @description Call Operation on logo Click: Header
 */
export const onLogoClickOperation = (): void => {
    gtmOperationPush({
        'event': 'addEvents_useNavigations',
        'event_id': 'd-v95-e1',
        'event_cat': 'TopBar',
        'event_name': 'clickElement',
        'event_param': 'Logo',
    });
};

/**
 * @description Call Operation on switch lang Click: Header
 * @param {string} language
 */
export const onSwitchLanguageClickOperation = (language: string): void => {
    gtmOperationPush({
        'event': 'addEvents_useNavigations',
        'event_id': 'd-v95-e2',
        'event_cat': 'TopBar',
        'event_name': 'clickElement',
        'event_param': `Lang / ${language}`,
    });
};

/**
 * @description Call operation on Notification button click
 * @param {string} message
 */
export const onNotificationLinkClick = (message: string): void => {
    gtmOperationPush({
        'event': 'addEvents_useNavigations',
        'event_id': 'd-v81-e1',
        'event_cat': 'RedLine',
        'event_name': 'clickElement',
        'event_param': message,
    });
};

/**
 * @description Call Operation on additional menu Click: Header
 * @param {string} message
 */
export const additionalButtonsClickOperation = (message: string): void => {
    gtmOperationPush({
        'event': 'addEvents_useNavigations',
        'event_id': 'd-v81-e3',
        'event_cat': 'TopBar',
        'event_name': 'clickElement',
        'event_param': message,
    });
};

/**
 * @description Call Operation on top menu Click: Header
 * @param {string} name
 * @param {string} menu
 */
export const tabTopClickOperation = (name: string, menu: string): IGTMOperation => ({
    'event': 'addEvents_useNavigations',
    'event_id': 'd-v81-e4',
    'event_cat': menu,
    'event_name': 'clickElement',
    'event_param': 'Tab / ' + name,
});

/**
 * @description Call Operation on footer menu Click: Footer
 * @param {string} name
 * @param {string} menu
 */
export const tabFooterClickOperation = (name: string, menu: string): void => {
    gtmOperationPush({
        'event': 'addEvents_useNavigations',
        'event_id': 'd-v81-e22',
        'event_cat': menu,
        'event_name': 'clickElement',
        'event_param': name,
    });
};

/**
 * @description Call Operation on tips Click: Header
 */
export const tipsClickOperation = (): void => {
    gtmOperationPush({
        'event': 'addEvents_useNavigations',
        'event_id': 'd-v81-e6',
        'event_cat': 'TopMenu',
        'event_name': 'clickElement',
        'event_param': 'Справка',
    });
};

/**
 * @description Call Operation on open prepayment modal Click: Bottom menu
 * @param {string} sum
 */
export const popUpPrepaymentOperation = (sum: string): IGTMOperation => ({
    'event': 'addEvents_makeConversions',
    'event_id': 'd-v81-e15',
    'event_cat': 'popUp / Счёт на предоплату',
    'event_name': 'submit',
    'event_param': sum,
});

/**
 * @description Call Operation on prepayment successful response: Bottom menu
 * @param {string} sum
 */
export const successfulPrepaymentOperation = (sum: string): IGTMOperation => ({
    'event': 'addEvents_makeConversions',
    'event_id': 'd-v81-e16',
    'event_cat': 'popUp / Счёт на предоплату',
    'event_name': 'sendSuccess',
    'event_param': sum,
});

/**
 * @description Call Operation on prepayment error response: Bottom menu
 * @param {string} error
 */
export const errorPrepaymentOperation = (error: string): IGTMOperation => ({
    'event': 'addEvents_makeConversions',
    'event_id': 'd-v81-e17',
    'event_cat': 'popUp / Счёт на предоплату',
    'event_name': 'sendError1',
    'event_param': error,
});

/**
 * @description Call Operation on inquiry attach file click: Inquiry Modal
 */
export const inquiryAddFileClickOperation = (): void => {
    gtmOperationPush({
        'event': 'addEvents_makeConversions',
        'event_id': 'd-v81-e18',
        'event_cat': 'popUp / ' + getLocation() + ' / Создать обращение',
        'event_name': 'attachFile',
        'event_param': '(empty)',
    });
};

/**
 * @description Call Operation on inquiry create button click: Inquiry Modal
 */
export const inquiryCreateClickOperation = (): void => {
    gtmOperationPush({
        'event': 'addEvents_makeConversions',
        'event_id': 'd-v81-e19',
        'event_cat': 'popUp / ' + getLocation() + ' / Создать обращение',
        'event_name': 'submit',
        'event_param': '(empty)',
    });
};

/**
 * @description Call Operation on inquiry successful response: Inquiry Modal
 */
export const inquirySuccessfulCreateOperation = (): void => {
    gtmOperationPush({
        'event': 'addEvents_makeConversions',
        'event_id': 'd-v81-e20',
        'event_cat': 'popUp / ' + getLocation() + ' / Создать обращение',
        'event_name': 'sendSuccess',
        'event_param': '(empty)',
    });
};

/**
 * @description Call Operation on inquiry error response: Inquiry Modal
 * @param {string} error
 */
export const inquiryErrorCreateOperation = (error: string): void => {
    gtmOperationPush({
        'event': 'addEvents_makeConversions',
        'event_id': 'd-v81-e21',
        'event_cat': 'popUp / ' + getLocation() + ' / Создать обращение',
        'event_name': 'sendError1',
        'event_param': error,
    });
};

export const clickOnBreadCrumbs = (text: string): IGTMOperation => ({
    'event': 'addEvents_useNavigations',
    'event_id': 'd-v395-e9',
    'event_cat': 'BreadCrumbs',
    'event_name': 'clickNavigationTab',
    'event_param': text,
});

const urlRegex = [
    { regex: /\/account\/accountInfo/, label: 'Лицевые счета/Карточка' },
    { regex: /\/accounts/, label: 'Лицевые счета' },
    { regex: /\/subscribers\/mobile/, label: 'Мобильная связь' },
    { regex: /\/subscribers\/fixed/, label: 'Фиксированная связь' },
    { regex: /\/subscriber\/info/, label: 'Абонент/Карточка' },
];

const getLocation = (): string | undefined => {
    const currentLocation = window.location.pathname;
    for (const regex of urlRegex) {
        if (regex.regex.test(currentLocation)) {
            return regex.label;
        }
    }
};
